import { useContext, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BusinessProfileContext } from 'src/business/context/BusinessProfileContext'
import {
  getBodyForSavingCompanyProfileBusiness,
  getPriceStringBusinessPlan,
  getTotalPricePEPM,
  prepareGroupSessionOptionsBusiness,
  prepareOneOnOneOptionsBusiness,
} from 'src/business/helpers/helperFunctions'
import {
  getBPDataWithoutParams,
  postBPDataWithoutParams,
  putBPDataWithoutParams,
} from 'src/business/services'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import {
  APP_CONSTANTS,
  currency_list,
  INDUSTRY_TYPE_IN_BUSINESS_LIST,
  ROLE_IN_BUSINESS_LIST,
  TABS_IN_BUSINESS_ONBOARDING
} from 'src/consumer/constants'
import { platformWeb } from 'src/consumer/services'
import { displayRazorpay } from 'src/consumer/services/paymentService'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { v4 as uuidv4 } from 'uuid'
import {
  BusinessOnboardingInputComponent,
  BusinessOnboardingSingleSelect,
} from './BusinessOnboardingComponents'
import {
  BOBPlanAppAccessContainer,
  BOBPlanRecommendedTag,
  BusinessOnboardingBody,
  BusinessOnboardingBodyDescription,
  BusinessOnboardingBodyDescriptionList,
  BusinessOnboardingBodyHeading,
  BusinessOnboardingBodyPlan,
  BusinessOnboardingBodyPlanSelectorText,
  BusinessOnboardingBodyPriceSpan,
  BusinessOnboardingBodyTitle,
  BusinessOnboardingHeader,
  BusinessOnboardingHeaderTabs,
  BusinessOnboardingHeaderTabsShapeOne,
  BusinessOnboardingMain,
  BusinessOnboardingPage,
  BusinessOnboardingSubmitButton
} from './BusinessOnboardingStyles'
import greenTick from 'src/assets/green_tick.png'

export default function BusinessOnboarding() {
  const { profileData } = useContext(BusinessProfileContext)
  const navigate = useNavigate()
  const availableRoles = getAllRolesInBoolean()

  // tab related
  const [selectedTab, setSelectedTab] = useState(TABS_IN_BUSINESS_ONBOARDING[0]?.id)
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false)

  // organization details related
  const [organizationName, setOrganizationName] = useState()
  const [numberOfEmployees, setNumberOfEmployees] = useState()
  const [roleInBusiness, setRoleInBusiness] = useState(ROLE_IN_BUSINESS_LIST[0]?.id)
  const [industryType, setIndustryType] = useState(INDUSTRY_TYPE_IN_BUSINESS_LIST[0]?.id)

  // plans related
  const [plansData, setPlansData] = useState()
  const [selectedPlanId, setSelectedPlanId] = useState()
  const [numberOfOneOnOneSessionsForPlanIds, setNumberOfOneOnOneSessionsForPlanIds] = useState()
  const [numberOfGroupSessionsForPlanIds, setNumberOfGroupSessionsForPlanIds] = useState()

  const selectedPlanData = plansData?.find((item) => item?.id === selectedPlanId)

  const handleBack = () => {
    if (selectedTab === 'plan') {
      setSelectedTab('organization')
    } else if (selectedTab === 'review') {
      setSelectedTab('plan')
    }
  }

  const handleNext = async () => {
    if (selectedTab === 'organization') {
      setSubmitButtonLoading(true)
      const responseSaveProfile = await postBPDataWithoutParams(
        '/api/secure/bp/company-profile/save',
        getBodyForSavingCompanyProfileBusiness(organizationName, numberOfEmployees),
      )
      setSubmitButtonLoading(false)
      if (responseSaveProfile?.status === 200 && responseSaveProfile?.data?.subscriptionPlans) {
        let plans = []
        responseSaveProfile?.data?.subscriptionPlans?.forEach((planItem) => {
          if (
            planItem?.id &&
            planItem?.currency &&
            planItem?.liveSessionsPSPMMinimum &&
            planItem?.liveSessionsPSPMMultiple &&
            planItem?.liveSessionsPSPMMultipleDiscount &&
            planItem?.liveSessionsPSPMPrice &&
            planItem?.groupSessionMinimum &&
            planItem?.groupSessionMultiple &&
            planItem?.groupSessionMultipleDiscount &&
            planItem?.groupSessionUnitPrice
          )
            // not honoring plans with incomplete data
            // add whatever must be in a plan
            plans.push({
              // currency: 'INR'
              ...planItem,
            })
        })
        // preselected id:
        const itemPreselected = plans?.find((item) => item?.preselected)
        setSelectedPlanId(itemPreselected ? itemPreselected?.id : plans?.[0]?.id)
        // picking preselected and keeping it at top:
        if (itemPreselected) {
          plans = [itemPreselected, ...plans.filter((item) => item.id !== itemPreselected.id)]
        }
        plans?.forEach((planItem) => {
          setNumberOfOneOnOneSessionsForPlanIds((prev) => ({
            ...prev,
            [planItem?.id]: prepareOneOnOneOptionsBusiness(planItem)?.find(
              (item) => item?.bestValue,
            )?.id,
          }))
          setNumberOfGroupSessionsForPlanIds((prev) => ({
            ...prev,
            [planItem?.id]: prepareGroupSessionOptionsBusiness(planItem)?.find(
              (item) => item?.bestValue,
            )?.id,
          }))
        })
        setPlansData(plans)
        setSelectedTab('plan')
      } else {
        toast.error('Please try again')
      }
    } else if (selectedTab === 'plan') {
      setSelectedTab('review')
    } else if (selectedTab === 'review') {
      await createOrderAndPay()
    }
  }

  async function createOrderAndPay() {
    const clientTransactionId = uuidv4()
    const livePEPMToServer = numberOfOneOnOneSessionsForPlanIds?.[selectedPlanId]
    const groupSessionsPerMonthToServer = numberOfGroupSessionsForPlanIds?.[selectedPlanId]
    const dataToSubmit = {
      seats: numberOfEmployees,
      liveSessionsPerSeatPerMonth: livePEPMToServer,
      monthlyGroupSessions: groupSessionsPerMonthToServer,
      successManagerTaken: false,
    }
    const response = await putBPDataWithoutParams(
      `/api/secure/bc/purchase/business-bundle?paymentGateway=razorpay&planId=${selectedPlanId}&seats=${numberOfEmployees}&livePSPM=${parseInt(
        livePEPMToServer,
      )}&groupSessionsPerMonth=${groupSessionsPerMonthToServer}&clientTransactionId=${clientTransactionId}&platform=${platformWeb}&paymentMethod=upi`,
      dataToSubmit,
    )
    if (response?.status === 200) {
      await displayRazorpay(response?.data)
    }
  }

  async function setInitialData() {
    const responseProfilePricing = await getBPDataWithoutParams(
      '/api/secure/bp/profile-and-pricing/response',
    )
    if (
      responseProfilePricing?.status === 200 &&
      isCommonResponseSuccessful(responseProfilePricing?.data?.code)
    ) {
      setOrganizationName(responseProfilePricing?.data?.data?.companyProfile?.companyName || '')
      if (responseProfilePricing?.data?.data?.companyProfile?.desiredSeats) {
        setNumberOfEmployees(responseProfilePricing?.data?.data?.companyProfile?.desiredSeats)
      }
    } else {
      console.log('Unable to fetch plans and profile')
    }
  }

  useEffect(() => {
    setInitialData()
  }, [])

  useEffect(() => {
    if (availableRoles.isBusinessCustomer) {
      navigate('/workplace')
    } else if (!availableRoles.isBusinessProspect) {
      navigate('/aumhum-for-workplace')
    }
  }, [])

  console.log(numberOfOneOnOneSessionsForPlanIds)
  console.log(numberOfGroupSessionsForPlanIds)
  console.log(numberOfEmployees)

  return (
    <BusinessOnboardingPage>
      <PublicAppHeader />
      <BusinessOnboardingMain>
        <BusinessOnboardingHeader>
          {TABS_IN_BUSINESS_ONBOARDING?.map((item) => (
            <BusinessOnboardingHeaderTabs selected={selectedTab === item?.id} key={item?.id}>
              <BusinessOnboardingHeaderTabsShapeOne
                style={{
                  position: 'absolute',
                  left: '0',
                  borderTop: `15px solid ${selectedTab === item?.id ? '#6e78e0' : '#f5f7fa'}`,
                  borderBottom: `15px solid ${selectedTab === item?.id ? '#6e78e0' : '#f5f7fa'}`,
                }}
              ></BusinessOnboardingHeaderTabsShapeOne>
              {item?.id === 'organization' &&
                (selectedTab === 'plan' || selectedTab === 'review') && (
                  <img src={greenTick} alt="tick" />
                )}
              {item?.id === 'plan' && selectedTab === 'review' && (
                <img src={greenTick} alt="tick" />
              )}
              {item?.title}
              <BusinessOnboardingHeaderTabsShapeOne
                style={{
                  position: 'absolute',
                  right: 0,
                  borderLeft: `7px solid ${selectedTab === item?.id ? '#c596fd' : '#f5f7fa'}`,
                }}
              ></BusinessOnboardingHeaderTabsShapeOne>
            </BusinessOnboardingHeaderTabs>
          ))}
        </BusinessOnboardingHeader>

        {(selectedTab === 'plan' || selectedTab === 'review') && (
          <BusinessOnboardingBody
            onClick={handleBack}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
          >
            Back
          </BusinessOnboardingBody>
        )}

        {selectedTab === 'organization' && (
          <BusinessOnboardingBody>
            <BusinessOnboardingBodyHeading>Organization Details</BusinessOnboardingBodyHeading>
            <BusinessOnboardingBodyDescription>
              Tell us about your organization
            </BusinessOnboardingBodyDescription>
            <div style={{ marginBottom: '20px' }}>
              <BusinessOnboardingInputComponent
                type="text"
                title="Organization Name"
                enteredValue={organizationName}
                setterFunction={(data) => setOrganizationName(data)}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <BusinessOnboardingInputComponent
                type="number"
                title="Number of Employees"
                enteredValue={numberOfEmployees}
                setterFunction={(data) => setNumberOfEmployees(data)}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <BusinessOnboardingSingleSelect
                idForSelection="your_role"
                title="Your Role"
                options={ROLE_IN_BUSINESS_LIST}
                selectedOptionId={roleInBusiness}
                setterFunction={(id) => setRoleInBusiness(id)}
              />
            </div>
            <div style={{ marginBottom: '40px' }}>
              <BusinessOnboardingSingleSelect
                idForSelection="your_industry"
                title="Your Industry"
                options={INDUSTRY_TYPE_IN_BUSINESS_LIST}
                selectedOptionId={industryType}
                setterFunction={(id) => setIndustryType(id)}
              />
            </div>
            <BusinessOnboardingSubmitButton
              style={{ marginBottom: '20px' }}
              onClick={handleNext}
              disabled={!(organizationName && numberOfEmployees && roleInBusiness && industryType)}
            >
              <span>Continue</span>
              {submitButtonLoading && <Spinner animation="border" variant="light" size="sm" />}
            </BusinessOnboardingSubmitButton>
          </BusinessOnboardingBody>
        )}

        {selectedTab === 'plan' && (
          <BusinessOnboardingBody>
            <BusinessOnboardingBodyHeading>Plan Pricing</BusinessOnboardingBodyHeading>

            <BusinessOnboardingBodyDescription>
              {/* {plansData?.length > 0
                ? 'Pricing is calculated based on the size of your organization.'
                : 'No plans available for desired Seats.'} */}
              <div style={{ marginBottom: '8px' }}>We have successfully captured your details.</div>
              <div>
                AUMHUM team will contact you. Meanwhile, you can write to {APP_CONSTANTS.SUPPORT_EMAIL} for
                any more queries.
              </div>
            </BusinessOnboardingBodyDescription>

            {plansData?.map((planItem, idx) => {
              return (
                <BusinessOnboardingBodyPlan
                  key={idx}
                  notselected={planItem?.id !== selectedPlanId}
                  onClick={() => setSelectedPlanId(planItem?.id)}
                >
                  {planItem?.preselected && (
                    <BOBPlanRecommendedTag>Recommended</BOBPlanRecommendedTag>
                  )}
                  <div>
                    <BusinessOnboardingBodyTitle>{planItem?.title}</BusinessOnboardingBodyTitle>
                  </div>
                  <div>
                    <BusinessOnboardingBodyPriceSpan>
                      {planItem?.description}
                    </BusinessOnboardingBodyPriceSpan>
                  </div>
                  {planItem?.id === selectedPlanId && (
                    <>
                      <div>
                        <BusinessOnboardingBodyPriceSpan main>
                          {getPriceStringBusinessPlan(
                            planItem?.currency,
                            getTotalPricePEPM(
                              numberOfOneOnOneSessionsForPlanIds?.[planItem?.id],
                              numberOfGroupSessionsForPlanIds?.[planItem?.id],
                              planItem,
                              numberOfEmployees,
                            ),
                          )}
                        </BusinessOnboardingBodyPriceSpan>
                      </div>
                      <div>
                        <BusinessOnboardingBodyPriceSpan>
                          (Per Employee Per Month)
                        </BusinessOnboardingBodyPriceSpan>
                      </div>
                      <div style={{ marginBottom: '20px' }}>
                        <BusinessOnboardingBodyPriceSpan>
                          Plan Validity : {`${planItem?.validity} days`}
                        </BusinessOnboardingBodyPriceSpan>
                      </div>

                      <BOBPlanAppAccessContainer>
                        <BusinessOnboardingBodyDescriptionList heading>
                          Account Features
                        </BusinessOnboardingBodyDescriptionList>
                        {planItem?.benefits?.map((benefit) => (
                          <BusinessOnboardingBodyDescriptionList key={benefit?.id}>
                            {`- ${benefit}`}
                          </BusinessOnboardingBodyDescriptionList>
                        ))}
                        <div style={{ marginTop: '20px' }}></div>
                        <BusinessOnboardingBodyPlanSelectorText bold gray>
                          {getPriceStringBusinessPlan(planItem?.currency, planItem?.finalPrice)}
                        </BusinessOnboardingBodyPlanSelectorText>
                      </BOBPlanAppAccessContainer>

                      <div style={{ marginBottom: '20px' }}>
                        <BusinessOnboardingSingleSelect
                          idForSelection="monthly_live_sessions"
                          title="Monthly Live Sessions per person"
                          options={prepareOneOnOneOptionsBusiness(planItem)}
                          selectedOptionId={numberOfOneOnOneSessionsForPlanIds?.[planItem?.id]}
                          setterFunction={(id) =>
                            setNumberOfOneOnOneSessionsForPlanIds((prev) => ({
                              ...prev,
                              [planItem?.id]: id,
                            }))
                          }
                          showPrices={true}
                          currency={planItem?.currency}
                        />
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <BusinessOnboardingSingleSelect
                          idForSelection="monthly_group_sessions"
                          title="Monthly Group Sessions"
                          options={prepareGroupSessionOptionsBusiness(planItem, numberOfEmployees)}
                          selectedOptionId={numberOfGroupSessionsForPlanIds?.[planItem?.id]}
                          setterFunction={(id) =>
                            setNumberOfGroupSessionsForPlanIds((prev) => ({
                              ...prev,
                              [planItem?.id]: id,
                            }))
                          }
                          showPrices={true}
                          currency={planItem?.currency}
                        />
                      </div>

                      <BusinessOnboardingSubmitButton onClick={handleNext}>
                        Select Plan
                      </BusinessOnboardingSubmitButton>
                    </>
                  )}
                </BusinessOnboardingBodyPlan>
              )
            })}
          </BusinessOnboardingBody>
        )}

        {selectedTab === 'review' && (
          <BusinessOnboardingBody>
            <BusinessOnboardingBodyHeading>Review your plan</BusinessOnboardingBodyHeading>

            <BusinessOnboardingBodyDescription>
              Access for your employees will start today.
            </BusinessOnboardingBodyDescription>

            <BusinessOnboardingBodyPlan>
              <div style={{ marginBottom: '10px' }}>
                <BusinessOnboardingBodyPriceSpan main>
                  {`${
                    currency_list.find(
                      (item) => item.code === selectedPlanData?.currency?.toLowerCase(),
                    )?.symbol
                  } ${(
                    getTotalPricePEPM(
                      numberOfOneOnOneSessionsForPlanIds?.[selectedPlanId],
                      numberOfGroupSessionsForPlanIds?.[selectedPlanId],
                      selectedPlanData,
                      numberOfEmployees,
                    ) * numberOfEmployees
                  )?.toFixed(2)}`}
                </BusinessOnboardingBodyPriceSpan>
              </div>

              <div style={{ marginBottom: '10px' }}>
                By continuing, you agree to our{' '}
                <a href={APP_CONSTANTS.APP_TERMS_URL} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href={APP_CONSTANTS.APP_PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms and Conditions
                </a>
              </div>

              <BusinessOnboardingSubmitButton style={{ marginBottom: '20px' }} onClick={handleNext}>
                Continue to checkout
              </BusinessOnboardingSubmitButton>

              <BOBPlanAppAccessContainer>
                <BusinessOnboardingBodyDescriptionList heading>
                  What you&apos;ll get
                </BusinessOnboardingBodyDescriptionList>
                {selectedPlanData?.benefits?.map((benefit) => (
                  <BusinessOnboardingBodyDescriptionList key={benefit?.id}>
                    {`- ${benefit}`}
                  </BusinessOnboardingBodyDescriptionList>
                ))}
                <BusinessOnboardingBodyDescriptionList>{`- ${numberOfOneOnOneSessionsForPlanIds?.[selectedPlanId]} sessions per employee every month`}</BusinessOnboardingBodyDescriptionList>
                <BusinessOnboardingBodyDescriptionList>{`- ${numberOfGroupSessionsForPlanIds?.[selectedPlanId]} group sessions every month`}</BusinessOnboardingBodyDescriptionList>
              </BOBPlanAppAccessContainer>
            </BusinessOnboardingBodyPlan>
          </BusinessOnboardingBody>
        )}
      </BusinessOnboardingMain>
    </BusinessOnboardingPage>
  )
}
